import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { playPriceChime } from "@/lib/priceChime";

// Expose the custom "Price" chime globally so the OneSignal Web SDK
// (initialised via an inline script in index.html) can trigger it when
// an incoming push has data.sound === "price".
try {
  window.__priceChime = { playPriceChime };
} catch (_) { /* SSR / restricted global */ }

// ─── Native debug overlay ───────────────────────────────────────────────
// When the React app crashes on iOS/Android (Capacitor) the WebView usually
// shows a fully blank screen with no way to inspect the error. This overlay
// renders synchronously into #root so we always see SOMETHING and catches
// runtime errors / unhandled promise rejections to display them on-screen.
//
// In production web (Safari/Chrome) the regular React tree replaces this
// overlay within a few ms, so users never see it. On the native bundle it
// becomes our crash console.
// ────────────────────────────────────────────────────────────────────────

const rootEl = document.getElementById("root");

// Flag the index.html boot-probe so it knows the React bundle reached this point
window.__SAOR_REACT_BOOTED__ = true;

// Tracks whether React successfully rendered its first tree. Once `true`,
// async errors (image loads, ad-blocked CDN scripts, late promise rejections,
// …) are silently ignored — the app keeps running normally.
let __reactMounted = false;

function showFatal(label, payload) {
  try {
    const detail =
      typeof payload === "string"
        ? payload
        : (payload && (payload.stack || payload.message)) ||
          JSON.stringify(payload);
    rootEl.innerHTML = `
      <div style="
        font-family: -apple-system, BlinkMacSystemFont, sans-serif;
        padding: 24px;
        color: #fff;
        background: #0f172a;
        min-height: 100vh;
        white-space: pre-wrap;
        word-break: break-word;
        overflow: auto;
      ">
        <h2 style="color:#f87171;margin:0 0 12px 0;font-size:18px;">${label}</h2>
        <div style="font-size:12px;line-height:1.5;color:#cbd5e1;">${
          detail || "(no details)"
        }</div>
        <div style="margin-top:24px;font-size:11px;color:#64748b;">
          Backend: ${process.env.REACT_APP_BACKEND_URL || "(not set)"}
        </div>
      </div>
    `;
  } catch (_) {
    /* swallow */
  }
}

// Initial breadcrumb — replaced by React almost immediately on success.
rootEl.innerHTML = `
  <div style="
    font-family: -apple-system, BlinkMacSystemFont, sans-serif;
    padding: 48px 24px;
    color: #94a3b8;
    background: #0f172a;
    min-height: 100vh;
    text-align: center;
  ">
    Saor — chargement…
  </div>
`;

window.addEventListener("error", (e) => {
  // Once React has rendered, swallow async errors so a single broken image
  // or a CDN script blocked by an ad-blocker never destroys the live UI.
  if (__reactMounted) return;
  // Many cross-origin scripts surface as a useless "Script error." with no
  // details — also ignore them on the very first render so the app gets a
  // chance to mount even if a third-party widget threw.
  const msg = e?.error?.message || e?.message;
  if (!msg || /^script error/i.test(String(msg))) return;
  showFatal("JS error", e?.error || msg);
});
window.addEventListener("unhandledrejection", (e) => {
  if (__reactMounted) return;
  showFatal("Unhandled promise rejection", e?.reason || String(e));
});

try {
  const root = ReactDOM.createRoot(rootEl);
  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
  // Give React a couple of frames to commit; once committed we consider the
  // app "live" and stop blanking it out on async errors.
  setTimeout(() => {
    __reactMounted = true;
  }, 500);
} catch (err) {
  showFatal("React mount crash", err);
}

// Service Worker registration removed entirely.
// OneSignal Web (/OneSignalSDKWorker.js) registers its own dedicated worker
// for push delivery. A legacy generic /sw.js at scope `/` was hijacking the
// same scope and blocking background push delivery — it still auto-unregisters
// itself in its `install` handler for users who had it installed previously,
// no action required from them.
